import React from "react";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Tooltip from "@material-ui/core/Tooltip";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import config from "../../../config";
import "./plans.css";

const dollars = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD"
});

const hasPrice = value =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  Number.isFinite(Number(value));

const money = value =>
  hasPrice(value) ? dollars.format(Number(value)) : "Price unavailable";

function pricedOptions(options) {
  return (Array.isArray(options) ? options : [])
    .filter(option => option && hasPrice(option.monthly_total))
    .slice()
    .sort((a, b) => Number(a.monthly_total) - Number(b.monthly_total));
}

function termLength(months) {
  if (months % 12 === 0) {
    const years = months / 12;
    return (
      years + (years === 1 ? " year" : " years") + " (" + months + " months)"
    );
  }
  return months + (months === 1 ? " month" : " months");
}

function Arrow() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 12h16M13 5l7 7-7 7" />
    </svg>
  );
}

function ContinueLink({ href }) {
  return (
    <a className="signup-plans-continue" href={href}>
      Continue to choose my plan <Arrow />
    </a>
  );
}

function CancellationTerms({ option }) {
  const maximum = Number(option.cancellation_max || 0);
  const monthly = Number(option.cancellation_per_month || 0);
  const flat = Number(option.cancellation_flat || 0);
  if (maximum <= 0 || (monthly <= 0 && flat <= 0)) {
    return <span>No early cancellation fee.</span>;
  }
  return (
    <span>
      Early cancellation costs{" "}
      {monthly > 0 ? money(monthly) + " per remaining month" : ""}
      {monthly > 0 && flat > 0 ? " plus " : ""}
      {flat > 0 ? money(flat) + " flat fee" : ""}, up to {money(maximum)} in
      total.
    </span>
  );
}

function RenewalTerms({ option, contractname }) {
  return (
    <div className="signup-plans-renewal">
      <p>
        After your term, service renews month to month
        {contractname ? " under " + contractname : ""}.
        {hasPrice(option.renewal_monthly_total) &&
        Number(option.renewal_monthly_total) >= 0
          ? " The current renewal price for this package is " +
            money(option.renewal_monthly_total) +
            "/month. The regular price in effect at renewal will apply."
          : " The regular price in effect at renewal will apply. The current regular price for this package is unavailable."}
      </p>
      <p>
        At the end of your term, you may contact MCSnet to discuss a new
        contract. Any new contract is subject to the offers, eligibility and
        pricing available then and your agreement to its terms. A new contract
        or discount is not guaranteed.
      </p>
      <p>Renewal prices exclude extras and tax and may change.</p>
    </div>
  );
}

function PlanCard({ option, flexible, renewalContractname }) {
  const term = flexible ? 0 : Number(option.termmonths || 0);
  const adjustment = Number(option.discount_monthly || 0);
  const eligible = pricedOptions(option.eligiblepackages);
  const maximum = Number(option.cancellation_max || 0);
  const hasCancellation =
    maximum > 0 &&
    (Number(option.cancellation_per_month || 0) > 0 ||
      Number(option.cancellation_flat || 0) > 0);
  const installationAdjustment = Number(option.installation_credit || 0);

  return (
    <article
      className={
        "signup-plans-card" + (flexible ? " signup-plans-card-flexible" : "")
      }
      aria-label={option.name + ", " + option.contractname}
    >
      <div className="signup-plans-card-body">
        <p className="signup-plans-eyebrow">
          {term > 0
            ? term === 24
              ? "2-year agreement"
              : term + "-month agreement"
            : "Month to month"}
        </p>
        <h3>{option.name}</h3>
        {term > 0 && (
          <p className="signup-plans-contract">{option.contractname}</p>
        )}
        <p className="signup-plans-price">
          <strong>{money(option.monthly_total)}</strong>
          <span>/month</span>
        </p>
        {term > 0 && (
          <p className="signup-plans-term">
            For {term === 24 ? "2 years" : termLength(term)}.
          </p>
        )}
        {adjustment < 0 ? (
          <p className="signup-plans-discount">
            <s>{money(option.base_monthly)}</s>
            <span className="signup-plans-savings">
              Save {money(Math.abs(adjustment))}/month
            </span>
            {term > 0 && (
              <span className="signup-plans-savings-term">
                {" "}
                during your term
              </span>
            )}
          </p>
        ) : adjustment > 0 ? (
          <p className="signup-plans-price-note">
            {money(option.base_monthly)} base price + {money(adjustment)}{" "}
            monthly contract fee
          </p>
        ) : (
          <p className="signup-plans-price-note">
            {term > 0
              ? "Monthly price during your term"
              : "No fixed-term commitment"}
          </p>
        )}
        <ul className="signup-plans-features">
          {Number(option.downloadmbps) > 0 && (
            <li className="signup-plans-feature-primary">
              <strong>
                {(Number(option.downloadmbps) === 1000
                  ? 940
                  : Number(option.downloadmbps)
                ).toLocaleString("en-CA")}{" "}
                Mbps
              </strong>
              <span>maximum download</span>
            </li>
          )}
          <li className="signup-plans-feature-primary">
            <strong>
              {option.capgb === null
                ? "Unlimited"
                : hasPrice(option.capgb)
                ? Number(option.capgb).toLocaleString("en-CA") + " GB"
                : "See plan details"}
            </strong>
            <span>monthly data</span>
          </li>
          {Number(option.uploadmbps) > 0 && (
            <li>
              <strong>
                {(Number(option.uploadmbps) === 1000
                  ? 940
                  : Number(option.uploadmbps)
                ).toLocaleString("en-CA")}{" "}
                Mbps
              </strong>
              <span>maximum upload</span>
            </li>
          )}
          <li>
            <strong>
              {Number(option.installation_total) === 0 &&
              hasPrice(option.installation_total)
                ? "Free"
                : money(option.installation_total)}
            </strong>
            <span>installation</span>
          </li>
        </ul>
        <div className="signup-plans-obligations">
          {term > 0 && (
            <p className="signup-plans-renewal-price">
              {hasPrice(option.renewal_monthly_total) &&
              Number(option.renewal_monthly_total) >= 0 ? (
                <>
                  Then month to month: currently{" "}
                  <strong>{money(option.renewal_monthly_total)}/month</strong>{" "}
                  for this package. The regular price in effect at renewal will
                  apply.
                </>
              ) : (
                <>
                  Then month to month at the regular price in effect at renewal.
                  The current regular price for this package is unavailable.
                </>
              )}
            </p>
          )}
          <p>
            {hasCancellation
              ? "Early cancellation up to " + money(maximum)
              : "No early cancellation fee"}
          </p>
          {option.needautopay && <p>Automatic payments required</p>}
        </div>
      </div>
      <details className="signup-plans-details">
        <summary>Plan details &amp; conditions</summary>
        <div>
          <p>
            {term > 0
              ? term + "-month commitment. "
              : "No fixed-term commitment. "}
            <CancellationTerms option={option} />
          </p>
          {hasPrice(option.installation_fee) && (
            <p>
              Installation price: {money(option.installation_fee)}
              {installationAdjustment < 0
                ? ", less a " +
                  money(Math.abs(installationAdjustment)) +
                  " installation credit"
                : installationAdjustment > 0
                ? ", plus a " +
                  money(installationAdjustment) +
                  " contract setup fee"
                : ""}
              . Installation total: {money(option.installation_total)}.
            </p>
          )}
          {option.needautopay && (
            <p>Automatic payments are required for this plan.</p>
          )}
          {term > 0 && (
            <>
              <RenewalTerms
                option={option}
                contractname={renewalContractname}
              />
              {eligible.length > 0 && (
                <>
                  <p>
                    Future upgrades and downgrades stay within this agreement.
                    You can only move between these included packages:
                  </p>
                  <ul>
                    {eligible.map(item => (
                      <li key={item.id || item.packageid}>
                        {item.name} — {money(item.monthly_total)}/month
                      </li>
                    ))}
                  </ul>
                  {eligible.length === 1 ? (
                    <p>
                      This agreement includes one package; there is no
                      lower-priced package to downgrade to.
                    </p>
                  ) : (
                    <p>
                      This preview shows the lowest-priced package in this
                      agreement.
                    </p>
                  )}
                </>
              )}
            </>
          )}
          <p>
            Speeds shown are advertised maximums. Actual speeds may vary with
            the installed radio technology, signal quality, the Ethernet link
            between your connection and router (such as 100 Mbps, 1 Gbps or 2.5
            Gbps), and Wi-Fi signal strength and distance from the router.
          </p>
        </div>
      </details>
    </article>
  );
}

function StaticIpHelp() {
  const [open, setOpen] = React.useState(false);
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Tooltip
        arrow
        interactive
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        enterTouchDelay={0}
        leaveTouchDelay={10000}
        classes={{ tooltip: "signup-plans-static-ip-tooltip" }}
        title={
          <div>
            <strong>Do I need a static IP?</strong>
            <p>
              Every Internet package already includes a working dynamic IP
              address. It can change over time and is suitable for everyday
              browsing, streaming and gaming.
            </p>
            <p>
              A static IP address stays the same. It can be useful for remotely
              accessing equipment, hosting services, or a VPN or business
              network that needs a fixed address.
            </p>
            <p>Most homes don’t need one. This is an optional add-on.</p>
          </div>
        }
      >
        <button
          type="button"
          className="signup-plans-static-ip-help"
          aria-label="About static IP addresses"
          onClick={() => setOpen(true)}
          onKeyDown={event => {
            if (event.key === "Escape") setOpen(false);
          }}
        >
          <HelpOutlineIcon fontSize="small" />
        </button>
      </Tooltip>
    </ClickAwayListener>
  );
}

function ExtraCard({ option }) {
  const term = Number(option.termmonths || 0);
  const cancelAnytime =
    term <= 1 &&
    Number(option.cancellation_per_month || 0) === 0 &&
    Number(option.cancellation_flat || 0) === 0 &&
    Number(option.cancellation_max || 0) === 0;
  return (
    <article className="signup-plans-extra">
      <div className="signup-plans-extra-heading">
        <h3>{option.name}</h3>
        {(option.id === "static2" || option.id === "static5") && (
          <StaticIpHelp />
        )}
      </div>
      <p className="signup-plans-extra-price">
        <strong>{money(option.monthly_total)}</strong> /month
      </p>
      {hasPrice(option.setup_total) && Number(option.setup_total) !== 0 && (
        <p>{money(option.setup_total)} one-time setup</p>
      )}
      <p>
        {option.id === "router"
          ? "Month to month - cancel anytime"
          : term > 1
          ? term + "-month commitment"
          : cancelAnytime
          ? "Month to month. Cancel anytime with no cancellation fee for this add-on."
          : "Month-to-month add-on."}
      </p>
      {option.id === "router" ? (
        <p>
          Return the equipment when you cancel. A{" "}
          {money(option.cancellation_flat)} fee applies if it is not returned.
        </p>
      ) : !cancelAnytime ? (
        <p>
          <CancellationTerms option={option} />
        </p>
      ) : null}
      {option.needautopay && <p>Automatic payments required</p>}
      {option.contractname && (
        <p className="signup-plans-extra-contract">{option.contractname}</p>
      )}
    </article>
  );
}

function ReferralBenefit({ referral }) {
  if (
    !referral ||
    referral.status !== "applied" ||
    !hasPrice(referral.first_bill_credit) ||
    Number(referral.first_bill_credit) <= 0
  ) {
    return null;
  }

  return (
    <aside className="signup-plans-referral" aria-label="Your referral credit">
      <svg
        className="signup-plans-referral-icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm-4 9 2.5 2.5L16 9"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div>
        <h2>
          {money(referral.first_bill_credit)} credit added on your first bill
        </h2>
        {referral.displayname && (
          <p className="signup-plans-referral-name">
            Referred by {referral.displayname}
          </p>
        )}
        <p className="signup-plans-referral-note">
          A one-time referral credit. Your monthly plan price stays the same.
        </p>
      </div>
    </aside>
  );
}

export default function Fin({ promocatalog, signupreflink }) {
  const catalog =
    promocatalog &&
    typeof promocatalog === "object" &&
    !Array.isArray(promocatalog)
      ? promocatalog
      : {};
  const offers = pricedOptions(catalog.offers);
  const flexible = pricedOptions(catalog.monthtomonth);
  const extras = pricedOptions(catalog.extras);
  const available =
    !catalog.unavailable_reason && (offers.length > 0 || flexible.length > 0);
  const newUserUrl =
    (config.IS_LOCAL_ENV
      ? "http://localhost:3001"
      : "https://myaccount.mcsnet.ca") +
    "/newuser" +
    (signupreflink
      ? "?signupreflink=" + encodeURIComponent(signupreflink)
      : "");
  const renewalContractname = catalog.tier && catalog.tier.renew_contractname;

  return (
    <div className="signup-plans">
      <section
        className="signup-plans-intro"
        aria-labelledby="signup-plans-title"
      >
        <div>
          <p className="signup-plans-eyebrow">Your signup has started</p>
          <h1 id="signup-plans-title">Your Internet options</h1>
          <p className="signup-plans-intro-copy">
            {available
              ? "Find the right balance of speed, monthly data and price for your home."
              : "You're one step closer to getting connected."}
          </p>
        </div>
        <div className="signup-plans-next">
          <ContinueLink href={newUserUrl} />
          <p>
            Choose your plan and extras securely in MyAccount, then finish your
            pre-install checklist.
          </p>
        </div>
      </section>

      {available ? (
        <>
          <p className="signup-plans-preview-note">
            This is a preview for your address. No plan has been selected yet.
            Prices are in Canadian dollars, before tax. Extras are separate.
          </p>
          <ReferralBenefit referral={catalog.referral} />
          {offers.length > 0 && (
            <section
              className="signup-plans-section"
              aria-labelledby="signup-term-heading"
            >
              <div className="signup-plans-section-heading">
                <div>
                  <p className="signup-plans-eyebrow">Find your fit</p>
                  <h2 id="signup-term-heading">Save with a contract</h2>
                </div>
                <p>Choose a plan with a lower monthly price.</p>
              </div>
              <div className="signup-plans-grid">
                {offers.map(option => (
                  <PlanCard
                    key={option.id || option.packageid}
                    option={option}
                    renewalContractname={renewalContractname}
                  />
                ))}
              </div>
            </section>
          )}

          {flexible.length > 0 && (
            <details
              className="signup-plans-flexible"
              open={offers.length === 0}
            >
              <summary>
                <span>
                  <strong>Prefer month to month?</strong>
                  <span>
                    No fixed-term commitment. See all {flexible.length}{" "}
                    available {flexible.length === 1 ? "package" : "packages"}.
                  </span>
                </span>
                <span className="signup-plans-flexible-controls">
                  <span className="signup-plans-flexible-price">
                    From {money(flexible[0].monthly_total)}/month
                  </span>
                  <span className="signup-plans-flexible-action">
                    <span className="signup-plans-flexible-show">
                      View plans
                    </span>
                    <span className="signup-plans-flexible-hide">
                      Hide plans
                    </span>
                  </span>
                </span>
              </summary>
              <div className="signup-plans-grid">
                {flexible.map(option => (
                  <PlanCard
                    key={option.id || option.packageid}
                    option={option}
                    flexible
                  />
                ))}
              </div>
            </details>
          )}

          {extras.length > 0 && (
            <section
              className="signup-plans-section"
              aria-labelledby="signup-extras-heading"
            >
              <div className="signup-plans-section-heading">
                <div>
                  <p className="signup-plans-eyebrow">Optional extras</p>
                  <h2 id="signup-extras-heading">Make it your own</h2>
                </div>
                <p>
                  Add these in MyAccount. Each extra has its own terms, separate
                  from your Internet plan.
                  {extras.some(extra => extra.id === "static2") &&
                  extras.some(extra => extra.id === "static5")
                    ? " Choose either 2 or 5 static IP addresses."
                    : ""}
                </p>
              </div>
              <div className="signup-plans-extras-grid">
                {extras.map(option => (
                  <ExtraCard key={option.id} option={option} />
                ))}
              </div>
            </section>
          )}

          <section className="signup-plans-bottom" aria-label="Continue signup">
            <div>
              <h2>Your next step is simple</h2>
              <p>
                Choose a plan, review your total and terms, and continue your
                installation checklist in MyAccount.
              </p>
            </div>
            <ContinueLink href={newUserUrl} />
          </section>
          <p className="signup-plans-footnote">
            Availability and pricing are checked again before you save your
            selection.
          </p>
        </>
      ) : (
        <section className="signup-plans-unavailable" role="status">
          <h2>We'll check your plan options in MyAccount</h2>
          <p>
            The plan preview isn't available right now. Continue securely to
            review availability and complete your next steps.
          </p>
          <ContinueLink href={newUserUrl} />
        </section>
      )}
    </div>
  );
}
